<script lang="ts" setup>
import OrderAPI from '~/api/order';
import googleIcon from '~/assets/images/payments/google.png';
import masterCardIcon from '~/assets/images/payments/master-card.png';
import visaIcon from '~/assets/images/payments/visa.png';

const TEST_ID = 'order-payment-methods';

const { t } = useI18n();
const orderStore = useOrderStore();
const modalStore = useModalStore();

const selectedType = ref('');

const { execute } = useRequest(OrderAPI.setPayment, () => ({
  body: { type: selectedType.value },
}));

const isEnabled = computed(() => !!orderStore.payment.variants.length);
const onlinePayment = computed(() =>
  orderStore.payment.variants.find((el) => el.type === 'online'),
);
const cardPayment = computed(() =>
  orderStore.payment.variants.find((el) => el.type === 'card'),
);
const cashPayment = computed(() =>
  orderStore.payment.variants.find((el) => el.type === 'cash'),
);

const onSelectPaymentType = (type?: string) => {
  if (!type) return;

  selectedType.value = type;
  execute()
    ?.then(orderStore.updateOrderState)
    .catch(() => {
      modalStore.showError({
        title: t('message.error'),
        action: t('message.action'),
      });
    });
};

watch(
  () => orderStore.payment.current,
  (v) => {
    selectedType.value = v?.type || '';
  },
);
</script>

<template>
  <div class="orderPaymentMethods">
    <div
      class="orderPaymentMethods__header"
      :class="{ orderPaymentMethods__header_enabled: isEnabled }"
    >
      {{ t('title') }}
    </div>

    <BaseCollapse
      :visible="isEnabled"
      :fixed-duration="300"
      content-class="orderPaymentMethods__content"
    >
      <BaseRadioPlate
        v-if="cardPayment"
        @update:checked="onSelectPaymentType(cardPayment?.type)"
        :checked="selectedType === cardPayment.type"
        value="card"
        name="orderPaymentMethod"
        :data-test-id="`${TEST_ID}-${cardPayment.type}`"
      >
        {{ cardPayment.name }}
      </BaseRadioPlate>
      <BaseRadioPlate
        v-if="cashPayment"
        @update:checked="onSelectPaymentType(cashPayment?.type)"
        :checked="selectedType === cashPayment.type"
        value="cash"
        name="orderPaymentMethod"
        :data-test-id="`${TEST_ID}-${cashPayment.type}`"
      >
        {{ cashPayment.name }}
      </BaseRadioPlate>
      <BaseRadioPlate
        v-if="onlinePayment"
        @update:checked="onSelectPaymentType(onlinePayment?.type)"
        :checked="selectedType === onlinePayment.type"
        value="online"
        name="orderPaymentMethod"
        :data-test-id="`${TEST_ID}-${onlinePayment.type}`"
      >
        {{ onlinePayment.name }}
        <div class="orderPaymentMethods__icons">
          <NuxtImg format="webp" :alt="t('icon.google')" :src="googleIcon" />
          <NuxtImg format="webp" :alt="t('icon.visa')" :src="visaIcon" />
          <NuxtImg
            format="webp"
            :alt="t('icon.master_card')"
            :src="masterCardIcon"
          />
        </div>
      </BaseRadioPlate>
    </BaseCollapse>
  </div>
</template>

<i18n>
ru:
  title: Доступные способы оплаты
  icon:
    google: Способ оплаты с использованием Google Pay
    visa: Способ оплаты с использованием Visa
    master_card: Способ оплаты с использованием Master Card
  message:
    error: Во время выбора способа оплаты произошла ошибка произошла ошибка
    action: Вернуться

en:
  title: Available payment methods
  icon:
    google: Payment method using Google Pay
    visa: Payment method using Visa
    master_card: Payment method using Master Card
  message:
    error: An error occurred while choosing the payment method
    action: Return
</i18n>

<style lang="scss">
.orderPaymentMethods {
  background: #ffffff;
  border-radius: 12px;
  padding: 0 20px;

  &__header {
    height: 64px;
    display: flex;
    align-items: center;
    font-size: 18px;
    line-height: 22px;
    letter-spacing: 0.2px;
    color: #c0c0c0;
    transition: color 0.3s ease;

    &_enabled {
      color: #121212;
    }
  }

  &__content {
    border-top: 1px solid rgba(18, 18, 18, 0.2);
    padding: 16px 0 26px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__icons {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
    padding: 0 9px;
  }
}
</style>
